<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="dashboard-admin-page">
    <div class="toolbar">
      <div class="toolbar-row">
        <div class="toolbar-left">
          <h2 class="toolbar-title">{{ $t("dashboardAdmin.title") }}</h2>
          <PrimeBadge
            v-if="personalEnabled"
            :value="
              currentScope === 'user'
                ? $t('dashboardAdmin.scopeUser')
                : $t('dashboardAdmin.scopeGlobal')
            "
            :severity="currentScope === 'user' ? 'info' : 'success'"
          />
        </div>
        <div class="toolbar-actions">
          <input
            ref="fileInput"
            type="file"
            accept=".json,application/json"
            style="display: none"
            @change="handleFileImport"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.loadConfig')"
            icon="ri-upload-line"
            severity="secondary"
            outlined
            @click="$refs.fileInput.click()"
          />
          <PrimeButton
            v-if="canRevert"
            :label="$t('dashboardAdmin.revertVersion')"
            icon="ri-history-line"
            severity="secondary"
            outlined
            @click="showVersionHistory = true"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.exportConfig')"
            icon="ri-download-line"
            severity="secondary"
            outlined
            @click="exportConfig"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.save')"
            icon="ri-save-line"
            @click="handleSave"
          />
        </div>
      </div>
    </div>

    <div class="editor-container">
      <DashboardEditor
        v-if="config"
        :config="config"
        :custom-block-types="customBlockTypes"
        :custom-route-options="customRouteOptions"
        @update:config="updateConfig"
      />
    </div>

    <!-- Version History Dialog -->
    <VersionHistoryDialog
      v-model:visible="showVersionHistory"
      :scope="currentScope"
      @revert="handleRevert"
    />

    <!-- Save Scope Dialog (only when personal is enabled) -->
    <PrimeDialog
      v-if="personalEnabled"
      v-model:visible="showSaveDialog"
      :modal="true"
      :header="$t('dashboardAdmin.saveDialogTitle')"
      :style="{ width: '95vw', maxWidth: '30rem' }"
    >
      <div class="save-dialog-content">
        <div>
          <p class="scope-label">{{ $t("dashboardAdmin.selectScope") }}</p>
          <div class="scope-options">
            <div class="scope-option">
              <PrimeRadioButton
                v-model="selectedScope"
                inputId="scope_user"
                value="user"
              />
              <label for="scope_user" class="scope-option-label">
                {{ $t("dashboardAdmin.scopeUser") }}
              </label>
            </div>
            <div class="scope-option">
              <PrimeRadioButton
                v-model="selectedScope"
                inputId="scope_global"
                value="global"
                :disabled="!isAdmin"
              />
              <label
                for="scope_global"
                class="scope-option-label"
                :class="{ 'text-disabled': !isAdmin }"
              >
                {{ $t("dashboardAdmin.scopeGlobal") }}
                <span v-if="!isAdmin" class="admin-only-hint">
                  ({{ $t("dashboardAdmin.adminOnly") }})
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="scope-info">
          <i class="ri-information-line scope-info-icon"></i>
          <span v-if="selectedScope === 'user'">
            {{ $t("dashboardAdmin.scopeUserInfo") }}
          </span>
          <span v-else>
            {{ $t("dashboardAdmin.scopeGlobalInfo") }}
          </span>
        </div>
      </div>
      <template #footer>
        <PrimeButton
          :label="$t('cancel')"
          icon="ri-close-line"
          text
          @click="showSaveDialog = false"
        />
        <PrimeButton
          :label="$t('dashboardAdmin.save')"
          icon="ri-save-line"
          @click="confirmSave"
        />
      </template>
    </PrimeDialog>

    <!-- Unsaved Changes Dialog -->
    <PrimeDialog
      v-model:visible="showUnsavedDialog"
      :modal="true"
      :closable="false"
      :header="$t('dashboardAdmin.unsavedChangesTitle')"
      :style="{ width: '95vw', maxWidth: '42rem' }"
    >
      <div class="unsaved-content">
        <i class="ri-error-warning-line unsaved-icon" aria-hidden="true"></i>
        <p class="unsaved-text">{{ $t("dashboardAdmin.unsavedChanges") }}</p>
      </div>
      <template #footer>
        <div class="unsaved-actions">
          <PrimeButton
            :label="$t('dashboardAdmin.unsavedDiscard')"
            icon="ri-delete-bin-line"
            severity="danger"
            @click="resolveUnsavedDialog('discard')"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.unsavedContinue')"
            icon="ri-edit-line"
            severity="secondary"
            outlined
            @click="resolveUnsavedDialog('continue')"
          />
          <PrimeButton
            :label="$t('dashboardAdmin.save')"
            icon="ri-save-line"
            @click="resolveUnsavedDialog('save')"
          />
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script>
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import RadioButton from "primevue/radiobutton";
import Badge from "primevue/badge";
import DashboardEditor from "@/components/dashboard/editor/DashboardEditor.vue";
import VersionHistoryDialog from "@/components/dashboard/editor/VersionHistoryDialog.vue";
import DashboardService from "@/services/DashboardService.js";
import { useConfigStore } from "@/stores/config";

export default {
  name: "DashboardAdminPage",
  components: {
    PrimeButton: Button,
    PrimeDialog: Dialog,
    PrimeRadioButton: RadioButton,
    PrimeBadge: Badge,
    DashboardEditor,
    VersionHistoryDialog,
  },
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
    customBlockTypes: {
      type: Array,
      default: () => [],
    },
    customRouteOptions: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      config: null,
      savedConfigSnapshot: null,
      currentScope: "global",
      showSaveDialog: false,
      showVersionHistory: false,
      showUnsavedDialog: false,
      unsavedDialogResolver: null,
      selectedScope: "global",
    };
  },
  computed: {
    personalEnabled() {
      const configStore = useConfigStore();
      return configStore.DASHBOARD_PERSONAL_ENABLED === true;
    },
    canRevert() {
      if (!this.personalEnabled) {
        return this.isAdmin;
      }
      return (
        this.currentScope === "user" ||
        (this.currentScope === "global" && this.isAdmin)
      );
    },
    isDirty() {
      if (!this.config || !this.savedConfigSnapshot) {
        return false;
      }
      return (
        JSON.stringify(this.config.configJson) !== this.savedConfigSnapshot
      );
    },
  },
  mounted() {
    if (this.personalEnabled) {
      this.currentScope = "user";
    }
    this.loadDashboard();
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    /**
     * Call this from the parent route component's beforeRouteLeave guard.
     * Returns true if navigation should proceed, false to cancel.
     */
    async handleBeforeRouteLeave() {
      if (!this.isDirty) {
        return true;
      }

      const choice = await this.promptUnsavedChanges();

      if (choice === "continue") {
        return false;
      }

      if (choice === "save") {
        await this.doSave(this.personalEnabled ? this.selectedScope : "global");
      }

      return true;
    },
    onBeforeUnload(event) {
      if (this.isDirty) {
        event.preventDefault();
      }
    },
    promptUnsavedChanges() {
      this.showUnsavedDialog = true;
      return new Promise((resolve) => {
        this.unsavedDialogResolver = resolve;
      });
    },
    resolveUnsavedDialog(choice) {
      this.showUnsavedDialog = false;
      if (this.unsavedDialogResolver) {
        this.unsavedDialogResolver(choice);
        this.unsavedDialogResolver = null;
      }
    },
    takeSnapshot() {
      if (this.config) {
        this.savedConfigSnapshot = JSON.stringify(this.config.configJson);
      }
    },
    async loadDashboard() {
      try {
        const result = await DashboardService.loadDashboard();

        if (!result) {
          this.initializeConfig();
          return;
        }

        const { config, scope } = result;
        this.config = Array.isArray(config) ? config[0] : config;
        if (this.personalEnabled) {
          this.currentScope = scope;
        }
        this.takeSnapshot();
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("dashboardAdmin.loadError"),
          life: 3000,
        });
        this.initializeConfig();
      }
    },
    initializeConfig() {
      this.config = {
        key: "default",
        configJson: {
          gridSettings: { columns: 3, rows: 4 },
          blocConfig: [],
        },
      };
      if (this.personalEnabled) {
        this.currentScope = "user";
      }
      this.takeSnapshot();
    },
    updateConfig(newConfig) {
      this.config = newConfig;
    },
    handleSave() {
      if (this.personalEnabled) {
        this.selectedScope = this.isAdmin ? this.currentScope : "user";
        this.showSaveDialog = true;
      } else {
        this.doSave("global");
      }
    },
    async confirmSave() {
      this.showSaveDialog = false;
      await this.doSave(this.selectedScope);
    },
    async doSave(scope) {
      try {
        await DashboardService.saveDashboard(this.config, scope);
        if (this.personalEnabled) {
          this.currentScope = scope;
        }
        this.takeSnapshot();
        this.$toast.add({
          severity: "success",
          summary: this.$t("success"),
          detail: this.$t("dashboardAdmin.saveSuccess"),
          life: 3000,
        });
      } catch (error) {
        console.error("Error saving dashboard:", error);
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("dashboardAdmin.saveError"),
          life: 3000,
        });
      }
    },
    exportConfig() {
      const dataStr = JSON.stringify(this.config, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dashboard-config-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    async handleRevert(version) {
      try {
        const comment = this.$t("dashboardAdmin.versionHistory.revertComment", {
          versionNumber: version.versionNumber,
        });
        const revertedConfig = await DashboardService.revertDashboardToVersion(
          version.id,
          this.currentScope,
          comment,
        );
        this.config = revertedConfig;
        this.takeSnapshot();
        this.$toast.add({
          severity: "success",
          summary: this.$t("success"),
          detail: this.$t("dashboardAdmin.versionHistory.revertSuccess"),
          life: 3000,
        });
      } catch (error) {
        console.error("Error reverting dashboard:", error);
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("dashboardAdmin.versionHistory.revertError"),
          life: 3000,
        });
      }
    },
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          let parsedConfig = JSON.parse(e.target.result);

          if (Array.isArray(parsedConfig)) {
            if (parsedConfig.length === 0) {
              throw new Error("Configuration array is empty");
            }
            parsedConfig = parsedConfig[0];
          }

          if (!parsedConfig.configJson) {
            throw new Error("Missing 'configJson' property");
          }
          if (!parsedConfig.configJson.gridSettings) {
            throw new Error("Missing 'gridSettings' in configJson");
          }
          if (!parsedConfig.configJson.blocConfig) {
            throw new Error("Missing 'blocConfig' in configJson");
          }
          if (!Array.isArray(parsedConfig.configJson.blocConfig)) {
            throw new Error("'blocConfig' must be an array");
          }

          const maxColNeeded = Math.max(
            ...parsedConfig.configJson.blocConfig.map(
              (b) => b.gridPosition.colEnd - 1,
            ),
            parsedConfig.configJson.gridSettings.columns,
          );
          const maxRowNeeded = Math.max(
            ...parsedConfig.configJson.blocConfig.map(
              (b) => b.gridPosition.rowEnd - 1,
            ),
            parsedConfig.configJson.gridSettings.rows,
          );

          if (
            maxColNeeded > parsedConfig.configJson.gridSettings.columns ||
            maxRowNeeded > parsedConfig.configJson.gridSettings.rows
          ) {
            parsedConfig.configJson.gridSettings.columns = maxColNeeded;
            parsedConfig.configJson.gridSettings.rows = maxRowNeeded;

            this.$toast.add({
              severity: "warn",
              summary: this.$t("dashboardAdmin.gridAutoAdjusted"),
              detail: this.$t("dashboardAdmin.gridAutoAdjustedDetail", {
                columns: maxColNeeded,
                rows: maxRowNeeded,
              }),
              life: 5000,
            });
          }

          this.config = parsedConfig;
          this.takeSnapshot();
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("dashboardAdmin.importSuccess"),
            life: 3000,
          });
        } catch (error) {
          console.error("Import error:", error);
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: `${this.$t("dashboardAdmin.importError")}: ${error.message}`,
            life: 5000,
          });
        }
      };
      reader.onerror = () => {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("dashboardAdmin.importError"),
          life: 3000,
        });
      };
      reader.readAsText(file);
      event.target.value = "";
    },
  },
};
</script>

<style scoped>
.dashboard-admin-page {
  background: var(--p-surface-ground);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar {
  padding: 0.75rem;
  background: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
}

.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toolbar-title {
  margin: 0;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

.editor-container {
  flex: 1;
  overflow: hidden;
}

.save-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.scope-label {
  margin-bottom: 0.75rem;
}

.scope-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.scope-option {
  display: flex;
  align-items: center;
}

.scope-option-label {
  margin-left: 0.5rem;
}

.text-disabled {
  color: var(--p-text-muted-color);
}

.admin-only-hint {
  font-size: 0.875rem;
}

.scope-info {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.scope-info-icon {
  margin-right: 0.5rem;
}

.unsaved-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.unsaved-icon {
  font-size: 2rem;
  color: var(--p-orange-500);
}

.unsaved-text {
  margin: 0;
}

.unsaved-actions {
  display: flex;
  gap: 0.5rem;
}
</style>
